import { useCallback, useEffect, useRef } from 'react';
import DataGridColumnWidthManager from '../util/DataGridColumnWidthManager';

interface UseDataGridColumnWidthsProps {
  gridId: string;
  defaultColumnWidths?: { [columnIndex: number]: number };
}

interface UseDataGridColumnWidthsReturn {
  gridRef: React.RefObject<any>;
  onColumnResized: (e: any) => void;
  onInitialized: (e: any) => void;
}

/**
 * Custom hook for managing DataGrid column widths globally
 * Preserves user-resized column widths for the entire session
 */
export const useDataGridColumnWidths = ({
  gridId,
  defaultColumnWidths = {}
}: UseDataGridColumnWidthsProps): UseDataGridColumnWidthsReturn => {
  const gridRef = useRef<any>(null);
  const widthManager = DataGridColumnWidthManager.getInstance();

  /**
   * Apply stored column widths to the grid
   */
  const applyStoredWidths = useCallback((gridInstance: any) => {
    if (!gridInstance) return;

    const storedWidths = widthManager.getColumnWidths(gridId);
    const widthsToApply = storedWidths || defaultColumnWidths;
    if (!widthsToApply || Object.keys(widthsToApply).length === 0) return;

    try {
      const columns = gridInstance.getVisibleColumns();
      columns.forEach((column: any, index: number) => {
        if (widthsToApply[index] !== undefined) {
          gridInstance.columnOption(column.dataField || index, 'width', widthsToApply[index]);
        }
      });
      if (!storedWidths && widthsToApply) {
        widthManager.saveColumnWidths(gridId, widthsToApply);
      }
    } catch (error) {
      console.warn(`Failed to apply stored widths for grid ${gridId}:`, error);
    }
  }, [gridId, widthManager, defaultColumnWidths]);

  /**
   * Handle column resize events
   */
  const onColumnResized = useCallback((e: any) => {
    if (!e.component) return;

    try {
      const columns = e.component.getVisibleColumns();
      const columnWidths: { [columnIndex: number]: number } = {};
      
      columns.forEach((column: any, index: number) => {
        if (column.width && typeof column.width === 'number') {
          columnWidths[index] = column.width;
        }
      });

      widthManager.saveColumnWidths(gridId, columnWidths);
    } catch (error) {
      console.warn(`Failed to save column widths for grid ${gridId}:`, error);
    }
  }, [gridId, widthManager]);

  /**
   * Handle grid initialization
   */
  const onInitialized = useCallback((e: any) => {
    if (!e.component) return;

    // Apply stored widths after a short delay to ensure grid is fully rendered
    setTimeout(() => {
      applyStoredWidths(e.component);
    }, 100);
  }, [applyStoredWidths]);

  /**
   * Apply stored widths when gridRef changes
   */
  useEffect(() => {
    if (gridRef.current?.instance) {
      applyStoredWidths(gridRef.current.instance);
    }
  }, [applyStoredWidths]);

  return {
    gridRef,
    onColumnResized,
    onInitialized
  };
};

export default useDataGridColumnWidths;